import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getCurrentUser, type User } from "@/features/auth/services/firebase";
import SettingsDangerZone from "./SettingsDangerZone";

/**
 * Read the current Firebase user without throwing when Firebase is not
 * configured (e.g. isolated render outside the app shell). Callers treat a
 * null result as "no password form".
 */
function safeCurrentUser(): User | null {
  try {
    return getCurrentUser();
  } catch {
    return null;
  }
}

/**
 * Extract a Firebase error code without an unchecked cast: narrows `err` to
 * an object with a string `code` field, falling back to "" when absent.
 */
function firebaseErrorCode(err: unknown): string {
  if (err && typeof err === "object" && "code" in err && typeof err.code === "string") {
    return err.code;
  }
  return "";
}

/**
 * Settings → Account: change-password form plus the "Danger zone" (GDPR
 * data export + account deletion, rendered by `SettingsDangerZone`).
 *
 * The change-password form is only shown for email/password accounts; federated
 * (e.g. Google) accounts manage credentials with their provider.
 */
export default function SettingsAccountSection() {
  const { t } = useTranslation(["settings", "common"]);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invalidField, setInvalidField] = useState<
    "current-password" | "confirm-password" | null
  >(null);
  const [saving, setSaving] = useState(false);

  const user = safeCurrentUser();
  const isPasswordUser = Boolean(
    user?.providerData.some((provider) => provider.providerId === "password"),
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setStatus(null);
    setError(null);
    setInvalidField(null);

    if (newPassword !== confirmPassword) {
      setError(t("settings:errors.passwordMismatch"));
      setInvalidField("confirm-password");
      return;
    }

    const current = getCurrentUser();
    if (!current || !current.email) {
      setError(t("settings:errors.updateFailed"));
      return;
    }

    setSaving(true);
    try {
      const credential = EmailAuthProvider.credential(
        current.email,
        currentPassword,
      );
      await reauthenticateWithCredential(current, credential);
      await updatePassword(current, newPassword);
      setStatus(t("settings:success.passwordChanged"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const code = firebaseErrorCode(err);
      // Wrong current password (legacy + modern Firebase codes) and the
      // recent-login requirement both mean "your current password did not
      // verify" from the user's perspective.
      if (
        code === "auth/wrong-password" ||
        code === "auth/invalid-credential" ||
        code === "auth/invalid-login-credentials" ||
        code === "auth/requires-recent-login"
      ) {
        setError(t("settings:errors.invalidPassword"));
        setInvalidField("current-password");
      } else {
        setError(t("settings:errors.updateFailed"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="space-y-4 rounded-lg border border-[var(--border)] bg-[var(--card)] p-6">
        <div>
          <h2 className="text-lg font-semibold text-[var(--foreground)]">
            {t("settings:security.changePassword")}
          </h2>
        </div>

        {isPasswordUser && (
          <form
            className="space-y-4"
            data-test="settings-change-password-form"
            onSubmit={handleSubmit}
          >
            <div>
              <label
                htmlFor="current-password"
                className="block text-sm font-medium text-[var(--foreground)]"
              >
                {t("settings:security.currentPassword")}
              </label>
              <Input
                autoComplete="current-password"
                className="mt-2"
                data-test="settings-current-password"
                id="current-password"
                onChange={(event) => setCurrentPassword(event.target.value)}
                required
                type="password"
                value={currentPassword}
                aria-invalid={invalidField === "current-password"}
                aria-describedby={
                  invalidField === "current-password" ? "settings-password-error" : undefined
                }
              />
            </div>

            <div>
              <label
                htmlFor="new-password"
                className="block text-sm font-medium text-[var(--foreground)]"
              >
                {t("settings:security.newPassword")}
              </label>
              <Input
                autoComplete="new-password"
                className="mt-2"
                data-test="settings-new-password"
                id="new-password"
                onChange={(event) => setNewPassword(event.target.value)}
                required
                type="password"
                value={newPassword}
              />
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium text-[var(--foreground)]"
              >
                {t("settings:security.confirmPassword")}
              </label>
              <Input
                autoComplete="new-password"
                className="mt-2"
                data-test="settings-confirm-password"
                id="confirm-password"
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                type="password"
                value={confirmPassword}
                aria-invalid={invalidField === "confirm-password"}
                aria-describedby={
                  invalidField === "confirm-password" ? "settings-password-error" : undefined
                }
              />
            </div>

            {status && (
              <Alert data-test="settings-password-status" variant="success">
                <AlertDescription>{status}</AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert
                data-test="settings-password-error"
                id="settings-password-error"
                variant="error"
              >
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end">
              <Button
                variant="cta"
                type="submit"
                disabled={saving}
                data-test="settings-change-password-submit"
              >
                {t("settings:security.updatePassword")}
              </Button>
            </div>
          </form>
        )}
      </section>

      <SettingsDangerZone />
    </div>
  );
}
